/**
 * The two groups each commodity's pricing points fall into, in the order every
 * surface lists them: hubs first, then the points load is priced at.
 *
 * One registry so the sidebar rows, the region overview's groups and filter,
 * the region routes and the hub page's relations all say the same thing in
 * both commodities. Gas's groups are the counterparts of power's, not copies
 * of them (see `PointKind.tsx`):
 *
 * - **Hubs** — power's trading hubs; gas's supply-area hubs (Henry Hub).
 * - **Load zones** (power) / **Delivery points** (gas) — where load is
 *   priced, each as a basis to a hub.
 *
 * Data only: no JSX, so `paths.ts`, `region-nav.ts` and the module file can
 * all read it without a cycle.
 */

import type { RegionTab } from "./paths";
import type { MarketCommodity, PointKindCode } from "@/types/market-intelligence";

export interface PointGroup {
  kind: PointKindCode;
  /** Plural, sentence case: the sidebar row, the group heading, the filter. */
  label: string;
  /** The region surface this group's filtered overview is served at. */
  tab: RegionTab;
}

export const POINT_GROUPS: Record<MarketCommodity, readonly [PointGroup, PointGroup]> = {
  power: [
    { kind: "hub", label: "Hubs", tab: "hubs" },
    { kind: "load_zone", label: "Load zones", tab: "load-zones" },
  ],
  gas: [
    { kind: "hub", label: "Hubs", tab: "hubs" },
    { kind: "delivery_point", label: "Delivery points", tab: "delivery-points" },
  ],
};

/**
 * The group a point falls in. Every point that is not a hub joins the second
 * group, so a kind the registry has never heard of still lands somewhere.
 */
export function pointGroupOf(
  commodity: MarketCommodity,
  kind: PointKindCode | undefined,
): PointGroup {
  const [hubs, rest] = POINT_GROUPS[commodity];
  return kind === "hub" ? hubs : rest;
}
